
export const fixStorageUrl = (url) => {
  if (!url) return url;
  if (typeof url !== 'string') return url;

  // If the url is pointing to our storage and missing the 'public/' segment, inject it.
  // E.g. https://...supabase.co/storage/v1/object/brand-assets/...
  // ->   https://...supabase.co/storage/v1/object/public/brand-assets/...
  const match = url.match(/\/storage\/v1\/object\/([^/]+)\//);
  if (match && match[1] !== 'public' && match[1] !== 'authenticated' && match[1] !== 'sign') {
     // The matched segment is likely the bucket name (e.g., brand-assets). Insert /public/ before it.
     return url.replace(`/storage/v1/object/${match[1]}/`, `/storage/v1/object/public/${match[1]}/`);
  }
  return url;
};

export const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

/**
 * Formats a date string to the user's selected timezone.
 * Handles legacy friendly names (e.g. "UTC-5 (Eastern Time)") by mapping to IANA zones.
 *
 * @param {string|Date} date - The date to format (ISO string or Date object).
 * @param {string} timezoneSetting - The timezone setting from the workspace (e.g. "UTC-5 (Eastern Time)").
 * @returns {string} - The formatted date string.
 */
export const formatDateTime = (date, timezoneSetting) => {
  if (!date || date === 'Never' || date === 'Just now') return date;

  const d = new Date(date);
  if (isNaN(d.getTime())) return date; // Return original if invalid date

  // Map legacy/friendly names to IANA timezones
  const timezoneMap = {
    'UTC-8 (Pacific Time)': 'America/Los_Angeles',
    'UTC-5 (Eastern Time)': 'America/New_York',
    'UTC+0 (GMT)': 'UTC',
    'UTC+1 (Central European Time)': 'Europe/Paris'
  };

  // Default to UTC if mapping fails, or use the setting if it's already a valid IANA zone (try/catch)
  let timeZone = timezoneMap[timezoneSetting] || 'UTC';

  // If the setting is not in the map, it might be a raw IANA zone or custom string
  // Test if it's valid
  if (!timezoneMap[timezoneSetting] && timezoneSetting) {
      try {
          Intl.DateTimeFormat(undefined, { timeZone: timezoneSetting });
          timeZone = timezoneSetting;
      } catch (e) {
          console.warn(`Invalid timezone setting: ${timezoneSetting}, falling back to UTC`);
          timeZone = 'UTC';
      }
  }

  try {
      return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          timeZone: timeZone,
          timeZoneName: 'short'
      }).format(d);
  } catch (error) {
      console.error("Error formatting date:", error);
      return d.toLocaleString(); // Fallback to browser local
  }
};

/**
 * Formats a date string to the user's selected timezone (Date only).
 *
 * @param {string|Date} date - The date to format.
 * @param {string} timezoneSetting - The timezone setting.
 * @returns {string} - The formatted date string.
 */
export const formatDateOnly = (date, timezoneSetting) => {
  if (!date) return '-';

  const d = new Date(date);
  if (isNaN(d.getTime())) return date;

  const timezoneMap = {
    'UTC-8 (Pacific Time)': 'America/Los_Angeles',
    'UTC-5 (Eastern Time)': 'America/New_York',
    'UTC+0 (GMT)': 'UTC',
    'UTC+1 (Central European Time)': 'Europe/Paris'
  };

  let timeZone = timezoneMap[timezoneSetting] || 'UTC';

  if (!timezoneMap[timezoneSetting] && timezoneSetting) {
      try {
          Intl.DateTimeFormat(undefined, { timeZone: timezoneSetting });
          timeZone = timezoneSetting;
      } catch (e) {
          timeZone = 'UTC';
      }
  }

  try {
      return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: timeZone
      }).format(d);
  } catch (error) {
      return d.toLocaleDateString();
  }
};
